<template>
    <div class="sign-contract-container">
        <v-alert v-if="errorMessage" type="error" dense>
            {{ $translate(errorMessage) }}
        </v-alert>

        <div v-if="attatchingSignature" class="d-flex justify-center align-center flex-column" style="height: 200px;">
            <div class="mb-4">{{ $translate('Generating signed contract') }}</div>

            <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="gettingContract" class="d-flex justify-center align-center" style="height: 200px;">
            <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="!contract">
            <v-alert type="error" dense>
                {{ $translate('CONTRACT_NOT_FOUND') }}
            </v-alert>
        </div>

        <div v-else-if="signed" class="sign-contract-content">
            <h3>
                <v-icon x-large color="success">mdi-checkbox-marked-circle-outline</v-icon>
                &nbsp;&nbsp;
                {{ $translate('CONTRACT_ALREADY_SIGNED') }}
            </h3>
            <br>
            {{ $translate('Contract {0} was signed at {1}').format(contract.name, contract.signedAt) }}<br>

            <v-btn
                class="btn-preview"
                outlined
                color="success"
                @click="previewContract"
            >
                {{ $translate('MEMBER_SHOW_CONTRACT') }}
            </v-btn>
        </div>

        <div v-else class="sign-contract-content">
            <h3>{{ $translate('MEMBER_SIGN_CONTRACT') }} {{ contract.name }}</h3>
            <p>{{ $translate('CONTRACT_INFOTEXT') }}</p>

            <v-btn
                class="btn-preview"
                outlined
                color="success"
                @click="previewContract"
            >
                {{ $translate('MEMBER_SHOW_CONTRACT') }}
            </v-btn>

            <div v-if="processing" class="bankid-signing-process">
                <qrcode-vue v-if="qrcode" :value="qrcode" :size="size" level="H" />
                <p class="bankid-instructions">
                    {{ $translate('Scan the QR code with your BankID app') }}
                </p>
                <v-btn
                    v-if="mobile && autostarttoken"
                    class="btn-open-bankid"
                    color="primary"
                    @click="openBankIDApp"
                >
                    {{ $translate('Open BankID on this device') }}
                </v-btn>
            </div>

            <div class="action-buttons">
                <v-btn
                    v-if="processing"
                    outlined
                    color="primary"
                    @click="cancel"
                >
                    {{ $translate('Cancel') }}
                </v-btn>

                <v-btn
                    :disabled="processing"
                    color="primary"
                    @click="startSigning"
                    v-if="!processing"
                >
                    {{ $translate('Sign with Mobile BankID') }}
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import {getMobileDeviceType, isExpoApp, isMobile} from '../util.js';
export default {
    name: "zoezi-sign-contract",

    zoezi: {
        title: "MEMBER_SIGN_CONTRACT",
        icon: "mdi-file-sign",
    },

    props: {
        contractId: {
            type: String,
            title: "Contract ID",
        },
    },

    components: {
        QrcodeVue,
    },

    data: () => ({
        contract: null,
        gettingContract: false,
        processing: false,
        errorMessage: null,
        autostarttoken: null,
        qrcode: null,
        pollContractTimer: null,
        size: 200, // Size of the QR code
    }),

    watch: {
        contractId: {
            immediate: true,
            handler() {
                this.getContract();
            }
        },
        attatchingSignature() {
            if (this.attatchingSignature) {
                this.pollContractTimer = setInterval(() => {
                    this.getContract();
                }, 1000);
            } else {
                clearInterval(this.pollContractTimer);
                this.pollContractTimer = null;
            }
        }
    },

    computed: {
        mobile() {
            return isMobile();
        },
        mobileType() {
            return getMobileDeviceType();
        },
        signed() {
            return this.contract?.signId && this.contract?.signedAt;
        },
        attatchingSignature() {
            return this.contract?.signId && !this.contract?.signedAt;
        }
    },

    methods: {
        getContract() {
            this.gettingContract = true;
            this.contract = null;
            this.processing = false;
            this.errorMessage = null;
            this.qrcode = null;
            this.$api.get('/api/public/contract/get', {contractId: this.contractId}).then(contract => {
                if (!contract.error) {
                    this.contract = contract;
                }
                this.gettingContract = false;
            }).catch(() => {
                this.gettingContract = false;
            });
        },
        openBankIDApp() {
            if (isExpoApp() && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'bankid', data: this.autostarttoken }));
            } else if (this.mobileType == 'ios') {
                let redirect_uri ='';
                window.location.href = 'https://app.bankid.com/?autostarttoken={0}&redirect={1}'.format(this.autostarttoken, encodeURIComponent(redirect_uri));
            } else if (this.mobileType == 'android') {
                window.open('bankid:///?autostarttoken={0}&redirect='.format(this.autostarttoken));
            } else {
                this.$store.commit('showSnackBar', {text: this.$translate('Device not supported')});
            }
        },
        previewContract() {
            const path = "/api/public/contract/download";
            const args = { contractId: this.contractId };
            if (isExpoApp()) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openFile', data: { api: {url: 'current', path, args }, type: 'pdf' } }));
            } else {
                const queryString = new URLSearchParams(args).toString();
                window.open(`${path}?${queryString}`, '_blank');
            }
        },
        startSigning() {
            this.processing = true;
            this.errorMessage = null;
            this.$api.post('/api/public/contract/sign/existing', {contractId: this.contractId}).then((response) => {
                if (!response.ok) {
                    this.errorMessage = 'Error during signing';
                    this.processing = false;
                    return;
                }

                response.json().then(data => {
                    if (data.aborted) {
                        this.errorMessage = 'Error';
                        this.processing = false;
                        return;
                    }

                    this.signId = data.signId;
                    this.autostarttoken = data.autostarttoken;

                    // Only open on Android or in app. Others will have to click the button
                    if (this.mobileType == 'android' || isExpoApp()) {
                        this.openBankIDApp(data.autostarttoken);
                    }
                    this.unsubscribeChannel = this.$api.subscribeChannel(data.signId, (resp) => {
                        if (resp.status == 'COMPLETE') {
                            this.processing = false;
                            this.qrcode = null;
                            this.unsubscribeChannel?.();
                            this.getContract();
                        } else if (resp.status == 'qrcode') {
                            this.qrcode = resp.qrcode;
                        } else if (resp.aborted) {
                            if (resp.status == 'ALREADY_IN_PROGRESS') {
                                this.errorMessage = this.$translate('BANKID_SIGNING_ALREADY_IN_PROGRESS');
                            } else if (resp.status == 'USER_CANCEL') {
                                this.errorMessage = this.$translate('USER_ABORTED_BANKID_SIGNING');
                            } else {
                                this.errorMessage = this.$translate('BANKID_ERROR_DURING_SIGNING');
                            }
                            this.processing = false;
                            this.unsubscribeChannel?.();
                            this.qrcode = null;
                        }
                    })
                })
            })
        },
        cancel() {
            this.errorMessage = null;
            this.processing = false;
        }
    },
    unmounted() {
        clearInterval(this.pollContractTimer);
    }
};
</script>

<style scoped>
.sign-contract-container {
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #f9f9f9;
    max-width: 600px;
    margin: 0 auto;
}

.sign-contract-content {
    text-align: center;
}

.btn-preview {
    margin: 10px 0;
}

.bankid-signing-process {
    margin: 20px 0;
}

.bankid-instructions {
    margin: 10px 0;
    font-style: italic;
}

.action-buttons {
    display: flex;
    justify-content: space-around;
    margin-top: 20px;
}
</style>
